import * as R from 'ramda'
import { isValidSquuid } from 'src/lib/util/board'

export const baseUserAttrs = [
  ':db/id',
  ':user/squuid',
  ':user/username',
  ':user/firstname',
  ':user/lastname',
  ':user/avatar'
]

export const baseNetworkAttrs = [
  ':db/id',
  ':network/squuid',
  ':network/title',
  ':network/name',
  {
    '@network/features :as :network/features': {},
    '@network/is-network-admin :as isNetworkAdmin': {},
    '@network/contract-end-date': {},
    ':network/settings': [
      ':db/id',
      ':network-settings/member-can-create-boards',
      ':network-settings/require-board-approval',
      ':network-settings/non-interactive-board-creation',
      ':network-settings/hide-branding',
      ':network-settings/enable-student-payment',
      ':network-settings/student-payment-price',
      ':network-settings/student-payment-license-duration'
    ]
  }
]

const gradingPeriodAttrs = [
  ':db/id',
  ':grading-period/weekly-max',
  ':grading-period/start-date',
  ':grading-period/end-date'
]

const boardGradingSettingsAttrs = [
  ':board-settings/hide-grading',
  ':board-settings/grading-enabled',
  ':board-settings/points-target-max'
]

const boardPointSettingsAttrs = [
  ':board-settings/alternative-points-system',
  ':board-settings/grade-passback-enabled',
  ...boardGradingSettingsAttrs
]

const boardTzSettingsAttrs = [
  ':board-settings/timezone',
  ':board-settings/tz-slug'
]

const boardGroupSettingsAttrs = [
  ':board-settings/group-visibility',
  ':board-settings/group-only-posting',
  ':board-settings/group-count',
  ':board-settings/groups-from-lms-prop'
]

const boardSettingsAttrs = [
  ':db/id',
  ':board-settings/groups-enabled',
  ':board-settings/topic-requirement',
  ':board-settings/nudge-settings',
  ':board-settings/read-only',
  ':board-settings/community-mentions-enabled',
  ':board-settings/permission-level',
  ':board-settings/discoverable',
  ':board-settings/is-template',
  ':board-settings/template-board',
  ...boardGroupSettingsAttrs,
  ...boardPointSettingsAttrs,
  ...boardTzSettingsAttrs
]

const boardTopicAttrs = [
  ':db/id',
  ':topic/label',
  ':topic/color',
  ':topic/enabled',
  ':topic/order',
  ':system/squuid'
]

const boardBadgeAttrs = [
  ':db/id',
  ':badge/name',
  ':badge/color',
  ':badge/point-value',
  ':badge/symbol',
  ':badge/author',
  ':badge/id',
  ':system/squuid'
]

const networkBoardAttrs = [
  ':db/id',
  ':board/squuid',
  ':board/title',
  {
    ':board/created-by': baseUserAttrs,
    ':board/network': [':network/title', { '@network/logo': {} }],
    // ':board/admins': [':db/id'],
    '@board/count-followers': {},
    '@board/count-posts': {}
  }
]

const networkTreeAttrs = [
  ':db/id',
  ':network/title',
  ':network/name',
  ':network/parent',
  {
    '@network/is-accessible :as isAccessible': {}
  }
]

const networkTreeLtiAttrs = networkName => [
  ':db/id',
  ':network/title',
  ':network/name',
  ':network/parent',
  {
    '@network/is-lti-accessible :as isAccessible': { networkName }
  }
]

const queryParamsToBoardId = (query, isEids = false) => {
  const { id } = query
  if (isValidSquuid(id)) {
    return { uuidStr: id }
  }
  if (isEids) {
    return { eids: [parseInt(id)] }
  }
  return { boardId: parseInt(id) }
}

export const queryParamsToUserId = (query, hasEids = false) => {
  const { user, id } = query
  const userId = user || id
  if (isValidSquuid(userId)) {
    return { uuidStr: userId }
  }
  if (hasEids) {
    return { eids: [parseInt(userId)] }
  }
  return { userId: parseInt(userId) }
}

const attachmentsFragment = () => [
  ':db/id',
  ':attachment/processing-status',
  ':attachment/title',
  ':attachment/uuid',
  ':attachment/type',
  ':attachment/mimetype',
  ':attachment/size',
  ':attachment/description'
]

export const adminLTIFragment = () => [
  {
    '@loggedInUser :as user': {
      keys: [
        '*',
        {
          '@user/all-networks': {
            keys: [':db/id', ':network/title', ':network/lti-credentials']
          },
          '@user/is-yd-admin': {}
        }
      ]
    }
  }
]

export const networkSelectFragment = () => [
  {
    '@loggedInUser :as user': {
      keys: [
        '*',
        ':user/squuid',
        {
          ':user/primary-email': [':email/text'],
          '@user/viewable-networks :as networks': {
            keys: networkTreeAttrs
          }
        }
      ]
    }
  }
]

const networkTrailFragment = ({ networkName }) => ({
  '@network/parent-network-trail :as trail': {
    keys: [':db/id', ':network/name', ':network/title'],
    networkName
  }
})

const invitationFragment = [
  ':db/id',
  ':system/created-at',
  ':invitation/permissions',
  {
    ':invitation/author': baseUserAttrs,
    ':invitation/board': [
      ':db/id',
      ':board/squuid',
      ':board/title',
      {
        ':board/network': [':db/id', ':network/name']
      }
    ]
  },
  {
    ':invitation/network': [':db/id', ':network/title', ':network/name']
  }
]

const userPendingInvitationsFragment = args => ({
  ...args,
  keys: invitationFragment
})

export const networkInvitationFragment = ({ networkName }) => [
  {
    '@loggedInUser :as user': {
      keys: [
        ...baseUserAttrs,
        {
          '@user/pending-invitations-count :as invitationsCount': {},
          '@user/pending-invitations :as invitations':
            userPendingInvitationsFragment(),
          '@user/all-networks :as network': {
            networkName,
            keys: [
              ...baseNetworkAttrs,
              ':network/brand-color',
              {
                ':network/owner': baseUserAttrs,
                '@network/user-invitations': {
                  keys: [
                    ':invitation/email',
                    ':invitation/status',
                    ':invitation/permissions',
                    ':invitation/updated-at',
                    {
                      ':invitation/author': baseUserAttrs,
                      ':invitation/network': baseNetworkAttrs
                    }
                  ]
                },
                '@network/pending-network-board-invitations': {
                  keys: [
                    ':invitation/email',
                    ':invitation/status',
                    ':invitation/permissions',
                    ':invitation/updated-at',
                    {
                      ':invitation/author': baseUserAttrs,
                      ':invitation/board': networkBoardAttrs
                    }
                  ]
                },
                ...networkTrailFragment({ networkName }),
                //-network
                '@network/is-network-admin :as isNetworkAdmin': {},
                ':network/admins :as admins': baseUserAttrs,
                '@network/access-info': {},
                '@network/brand-color': {}
              }
            ]
          }
        }
      ]
    }
  }
]

export const baseUserFragment = ({ networkName }, subFragment) => [
  {
    '@loggedInUser :as user': {
      keys: [
        ...baseUserAttrs,
        {
          ':user/networks': [
            ':db/id',
            ':network/name',
            ':network/title',
            ':network/squuid'
          ]
        },
        ':user/admin-caps',
        ':user/cover-photo',
        { ':user/emails': [':db/id', ':email/text', ':email/verified'] },
        ':user/primary-email',
        {
          '@user/is-yd-admin': {},
          '@user/segmentation-roles': {},
          '@user/is-network-member :as isNetworkMember': {
            networkName
          },
          '@user/is-network-guest': {
            networkName
          },
          '@user/network :as network': {
            networkName,
            keys: [
              ...baseNetworkAttrs,
              ':network/title',
              ':network/tz-slug',
              ':network/owner',
              ':network/brand-color',
              ':network/logo-url',
              {
                ...networkTrailFragment({ networkName }),
                '@network/timezone': {},
                '@network/logo': {},
                '@network/brand-color': {},
                '@network/features :as :network/features': {},
                '@network/date-format': {}
              }
            ]
          },
          ...subFragment
        }
      ]
    }
  }
]

export const userFactsFragment = ({
  alias,
  networkName,
  fromDate,
  toDate
}) => ({
  [`@loggedInUser :as ${alias || 'user'}`]: {
    keys: [
      {
        '@user/total-earned-points': { networkName, fromDate, toDate },
        '@user/reaction-received-count': {
          networkName,
          fromDate,
          toDate
        },
        '@user/comment-received-count': { networkName, fromDate, toDate }
      }
    ]
  }
})

const pointConfigConditionFragment = attrs => ({
  ':point-predicate/condition': [
    ':db/id',
    ':point-predicate-condition/type',
    ':point-predicate-condition/value',
    {
      ':point-predicate-condition/entity-ref': attrs
    }
  ]
})

const pointPredicatesFragment = () => [
  ':db/id',
  ':point-predicate/entity-attr',
  ':point-predicate/value',
  pointConfigConditionFragment(boardTopicAttrs)
]

export const pointConfigWithoutModifiersFragment = () => [
  ':db/id',
  ':system/created-at',
  {
    ':point-configuration/point-predicates': pointPredicatesFragment()
  }
]

export const pointConfigurationsFragment = () => [
  ':db/id',
  ':system/created-at',
  {
    ':point-configuration/point-predicates': pointPredicatesFragment(),
    ':point-configuration/point-modifiers': [
      ':db/id',
      ':point-modifier/type',
      ':point-modifier/entity',
      ':point-modifier/value'
    ]
  }
]

export const boardPointSettingsFragment = (subFragment = []) => [
  ':db/id',
  ':board/squuid',
  ':board/updated-at',
  {
    '@board/timezone': {},
    ':board/settings': [':db/id', ...boardPointSettingsAttrs],
    ':board/point-configurations': pointConfigurationsFragment(),
    ':board/grading-periods': gradingPeriodAttrs,
    ':board/badges': boardBadgeAttrs,
    ':board/copy-settings-from': [
      ':db/id',
      {
        ':board/settings': [
          ':db/id',
          ':board-settings/points-target-max',
          ':board-settings/grading-enabled',
          ':board-settings/alternative-points-system'
        ],
        ':board/grading-periods': gradingPeriodAttrs
      }
    ]
  },
  ...subFragment
]

export const userSettingsFragment = () => ({
  '@user/has-password': {},
  ':user/notification-settings :as notificationSettings': [
    ':db/id',
    ':notification-setting/type',
    ':notification-setting/board',
    ':notification-setting/channels'
  ],
  ':user/location': [':db/id', ':location/city', ':location/state']
})

export const userViewableUsersFragment = ({ query }, subFragment) => ({
  '@user/viewable-users :as profile': {
    ...queryParamsToUserId(query, true),
    keys: baseUserAttrs
  },
  ...subFragment
})

export const userCapsFragment = ({ query }) => ({
  '@user/viewable-users :as profile': {
    ...queryParamsToUserId(query, true),
    keys: [...baseUserAttrs, ':user/admin-caps']
  }
})

const boardNetworkFragment = () => [
  ':db/id',
  ':network/title',
  ':network/name',
  {
    ':network/owner': baseUserAttrs,
    '@network/enable-student-payment': {},
    '@network/student-payment-price': {},
    '@network/student-payment-license-duration': {},
    '@network/logo': {},
    '@network/brand-color': {},
    '@network/features :as :network/features': {},
    ':network/parent': [':db/id', ':network/title']
  },
  {
    ':network/settings': [':db/id', ':network-settings/require-board-approval']
  },
  ':network/brand-color',
  ':network/css-url',
  { ':network/default-board': [':db/id', ':board/squuid'] }
]

export const boardGradeSyncFragment = ({ _ }, subFragment) => ({
  '@board/lti-grades': {
    keys: [
      {
        ':lti-grade/user': baseUserAttrs
      },
      ':lti-grade/last-grade-passed',
      ':lti-grade/last-grade-passed-at'
    ]
  },
  ...subFragment
})

export const boardGradeSync3Fragment = ({ _ }, subFragment) => ({
  '@board/lti-3-grades': {
    keys: [
      {
        ':lti-3-grade/user': baseUserAttrs
      },
      ':lti-3-grade/last-grade-passed',
      ':lti-3-grade/last-grade-passed-at'
    ]
  },
  ...subFragment
})

export const userBoardIndexFragment = ({ query }, subFragment = {}) => ({
  '@user/all-boards :as board': {
    ...queryParamsToBoardId(query, true),
    keys: [
      ':db/id',
      ':board/squuid',
      ':board/title',
      ':board/disabled-emojis',
      {
        ':board/created-by': baseUserAttrs,
        ':board/network': boardNetworkFragment(),
        ':board/settings': boardSettingsAttrs
      },
      subFragment
    ]
  }
})

export const userBoardFragment = ({ query }, subFragment = {}) => ({
  '@user/follows-board': { ...queryParamsToBoardId(query) },
  '@user/can-view-board': { ...queryParamsToBoardId(query) },
  '@user/can-admin-board': { ...queryParamsToBoardId(query) },
  '@user/all-boards :as board': {
    ...queryParamsToBoardId(query, true),
    keys: [
      ':db/id',
      ':board/squuid',
      ':board/title',
      ':board/description',
      ':board/disabled-emojis',
      ':board/features',
      ':board/service-url',
      ':board/blocked-words',
      ':board/onboarding-dismissed',
      ':board/onboarding-steps-acknowledged',
      ':board/pending-approval',
      ':system/created-at',
      ':board/notification-users',
      ':board/lti-id',
      {
        ':board/payment-setting': [
          ':db/id',
          ':payment-setting/enable-student-payment',
          ':payment-setting/student-payment-price',
          ':payment-setting/student-payment-license-duration'
        ],
        ':board/grading-periods': gradingPeriodAttrs,
        ':board/point-configurations': pointConfigurationsFragment(),
        ':board/copy-settings-from': [
          ':db/id',
          {
            ':board/settings': [':db/id', ':board-settings/points-target-max'],
            ':board/grading-periods': [':db/id', ':grading-period/weekly-max']
          }
        ],
        ':board/lti-3-integrations': [
          ':db/id',
          ':board-lti-3-integration/lineitem-service-url',
          ':board-lti-3-integration/names-and-roles-url',
          ':board-lti-3-integration/lineitems'
        ],
        '@board/metrics': {},
        ':board/created-by': baseUserAttrs,
        '@board/timezone': {},
        '@board/user-invitations :as invitations': {
          keys: [
            ':invitation/email',
            ':invitation/status',
            ':invitation/permissions',
            ':invitation/updated-at',
            {
              ':invitation/author': baseUserAttrs,
              ':invitation/board': [
                ':board/title',
                ':db/id',
                ':board/squuid',
                ':board/network',
                ':board/created-by',
                ':board/admins'
              ]
            }
          ]
        },
        '@board/is-admin :as isBoardAdmin': {},
        '@board/is-admin': {},
        '@board/count-draft-posts': {},
        '@board/count-flagged-posts :as flaggedPostCount': {},
        '@board/count-join-requests': {},
        '@board/count-posts': {},
        ':board/network': boardNetworkFragment(),
        ':board/settings': boardSettingsAttrs,
        ':board/admins :as admins': baseUserAttrs,
        '@board/user-in-board :as userInBoard': {}
      },
      subFragment
    ]
  }
})

export const viewableNetworkFragment = (ctx, subFragment) => ({
  '@user/viewable-networks': {
    keys: baseNetworkAttrs
  },
  ...subFragment
})

export const boardCreateFollowedFragment = (
  { query, networkName },
  subFragment
) => ({
  '@user/copy-boards': {
    uuidStr: R.prop('templateId', query) ? R.prop('templateId', query) : null,
    networkName,
    keys: [
      ':db/id',
      ':board/squuid',
      ':board/title',
      ':board/created-by',
      ':board/lifecycle'
    ]
  },
  '@user/link-boards': {
    uuidStr: R.prop('templateId', query) ? R.prop('templateId', query) : null,
    networkName,
    keys: [':db/id', ':board/squuid', ':board/title', ':board/created-by']
  },
  '@user/viewable-sub-networks :as subnetworks': {
    networkName,
    keys: [...baseNetworkAttrs]
  },
  '@user/can-admin-network': {
    networkName
  },
  ...subFragment
})

export const boardDataFragment = (_, subFragment) => ({
  '@board/user-lti-grade': {
    keys: [
      ':db/id',
      ':lti-grade/sourcedid',
      ':lti-grade/user',
      ':lti-grade/last-grade-passed',
      ':lti-grade/last-grade-passed-at'
    ]
  },
  '@board/user-lti-3-grade': {
    keys: [
      ':db/id',
      ':lti-3-grade/user',
      ':lti-3-grade/last-grade-passed',
      ':lti-3-grade/last-grade-passed-at'
    ]
  },
  ...subFragment
})

export const boardGroupsFragment = (subQuery, subFragment) => ({
  ':board/groups': R.filter(Boolean, [
    ':db/id',
    ':group/name',
    ':system/squuid',
    subQuery
  ]),
  ...subFragment
})

export const boardGroupMembersFragment = (memberAttrs = [':db/id']) => ({
  ':group/members': memberAttrs
})

export const boardGroupedLogFragment = ({ query }) => ({
  '@board/grouped-log :as log': queryParamsToUserId(query),
  '@board/metrics :as userBoardMetrics': {
    boardId: parseInt(query.id),
    ...queryParamsToUserId(query)
  }
})

export const myPointsFragment = ({ query }) => ({
  '@board/grouped-log :as log': query.user ? { uuidStr: query.user } : {},
  '@board/metrics :as userBoardMetrics': query.user
    ? { uuidStr: query.user }
    : {},
  '@board/user-badges': {
    ...queryParamsToUserId(query),
    keys: [
      ':db/id',
      {
        ':post/parent': [':db/id', ':event/squuid'],
        ':post/badges': [
          ':db/id',
          ':system/created-at',
          {
            ':badge/id': [
              ':badge/name',
              ':badge/color',
              ':badge/point-value',
              ':badge/symbol'
            ],
            ':badge/author': baseUserAttrs
          }
        ]
      }
    ]
  }
})

export const boardDashboardPointsFragment = ({ query, networkName }) => {
  const params = queryParamsToBoardId(query)
  return {
    ':user/_follows :as followers': [
      ...baseUserAttrs,
      {
        '@user/badge-count-received': params,
        '@user/comment-count-received': params,
        '@user/user-created-post-count': params,
        '@user/user-given-reaction-count': params,
        '@user/user-received-reaction-count': params,
        '@user/user-comment-count': params,
        '@user/points-on-board': params,
        '@user/followed-boards :as boards': {
          networkName
        }
      }
    ]
  }
}

export const boardDashboardFragment = ({ query }) => ({
  '@board/community-health-summary': { ...queryParamsToBoardId(query) },
  '@board/count-active-followers': {},
  ':user/_follows :as followers': [...baseUserAttrs]
})

export const boardDashboardHealthCheckFragment = ({ query }) => ({
  ':board/badges': {
    keys: [
      ':db/id',
      ':badge/color',
      ':badge/name',
      ':badge/point-value',
      ':badge/symbol',
      ':system/created-at'
    ]
  },
  '@board/community-health-check': { boardId: parseInt(query.id) }
})

export const boardDashboardTopicsFragment = ({ query }) => ({
  '@board/count-posts': {},
  '@board/count-reactions': {},
  '@board/topic-counts': { boardId: parseInt(query.id) },
  '@board/topics-by-user': { boardId: parseInt(query.id) }
})

export const boardFilesFragment = () => ({
  '@board/user-lti-grade': {
    keys: [':db/id', ':lti-grade/sourcedid', ':lti-grade/user']
  },

  '@board/files :as files': {
    keys: [
      ':system/created-at',
      ':post/updated-at',
      {
        ':post/attachments': attachmentsFragment(),
        '@post/author': { keys: baseUserAttrs }
      }
    ]
  }
})

export const activityFragment = () => [
  ':db/id',
  ':event/squuid',
  ':post/title',
  { ':post/board': [':db/id', ':board/squuid'] },
  ':system/created-at',
  ':post/updated-at',
  { '@post/author': { keys: baseUserAttrs }, '@post/body-text': {} },
  { ':post/topics': boardTopicAttrs },
  {
    ':post/parent': [
      ':db/id',
      ':event/squuid',
      { '@post/author': { keys: baseUserAttrs } },
      { ':post/topics': boardTopicAttrs },
      { ':post/flags': [':db/id'] }
    ]
  },
  ':reaction/key',
  ':reaction/value',
  {
    ':badge/id': [
      ':db/id',
      ':badge/symbol',
      ':badge/color',
      ':badge/point-value',
      ':badge/name'
    ]
  },
  { ':badge/author': baseUserAttrs },
  { ':reaction/author': baseUserAttrs },
  {
    ':post/_badges': [
      ':db/id',
      ':event/squuid',
      { ':post/parent': [':db/id', ':event/squuid'] },
      { ':post/board': [':db/id', ':board/squuid'] },
      { '@post/author': { keys: baseUserAttrs } },
      { ':post/flags': [':db/id'] }
    ]
  },
  {
    ':post/_reactions': [
      ':db/id',
      ':event/squuid',
      { ':post/parent': [':db/id', ':event/squuid'] },
      { ':post/board': [':db/id', ':board/squuid'] },
      { '@post/author': { keys: baseUserAttrs } },
      { ':post/flags': [':db/id'] }
    ]
  },
  { ':post/flags': [':db/id'] }
]

export const boardActivityFragment = () => ({
  '@board/activity': {
    limit: 10,
    keys: activityFragment()
  }
})

const boardNetworkSettingsFragment = () => [
  ':db/id',
  ':network/title',
  ':network/name',
  {
    ':network/owner': baseUserAttrs,
    '@network/logo': {},
    '@network/brand-color': {},
    '@network/features :as :network/features': {},
    '@network/default-board :as defaultBoard': {
      keys: [
        ':board/title',
        ':board/squuid',
        {
          ':board/settings': boardSettingsAttrs,
          ':board/point-configurations': pointConfigurationsFragment(),
          ':board/grading-periods': gradingPeriodAttrs
        }
      ]
    },
    ':network/admins': [
      ':db/id',
      ':user/squuid',
      ':user/username',
      ':user/avatar'
    ],
    ':network/parent': [':db/id', ':network/title']
  },
  ':network/brand-color'
]

export const boardSettingsFragment = (ctx, subFragment) => ({
  ':board/notification-users': [':db/id'],
  ':board/network': boardNetworkSettingsFragment(),
  ...subFragment
})

export const userNetworkFragment = ({ networkName }, subFragment = {}) => ({
  '@user/can-admin-network': {
    networkName
  },
  '@user/network :as network': {
    networkName,
    keys: [
      ...baseNetworkAttrs,
      ':network/logo',
      ':network/parent',
      ':network/brand-color',
      ':network/tz-slug',
      ':network/is-root',
      {
        ':network/owner': baseUserAttrs,
        ...networkTrailFragment({ networkName }),
        '@network/logo': {},
        '@network/features :as :network/features': {},
        '@network/brand-color': {},
        '@network/timezone': {},
        '@network/access-info': {},
        '@network/is-network-admin :as isNetworkAdmin': {},
        '@network/user-invitations': {
          keys: [
            ':invitation/email',
            ':invitation/status',
            ':invitation/permissions',
            ':invitation/updated-at',
            {
              ':invitation/author': baseUserAttrs,
              ':invitation/network': [
                ':network/name',
                ':network/title',
                ':network/owner',
                ':network/admins'
              ]
            }
          ]
        },
        '@network/admins :as admins': {
          keys: baseUserAttrs
        }
      },
      subFragment
    ]
  }
})

export const userSubnetworksFragment = ({ networkName }) => ({
  '@user/viewable-networks :as networks': {
    keys: baseNetworkAttrs
  },
  '@user/viewable-sub-networks :as subnetworks': {
    networkName,
    keys: [
      ...baseNetworkAttrs,
      {
        '@network/logo': {}
      }
    ]
  }
})

export const networkCouponsFragment = () => ({
  '@global/all-coupons :as coupons': {
    keys: [
      ':db/id',
      ':coupon/isbn',
      ':system/created-at',
      {
        '@global/coupon-code-count :as totalCount': {},
        '@global/coupon-code-redeemed-count :as redeemedCount': {},
        ':coupon/created-by': baseUserAttrs
      }
    ]
  }
})

export const networkBoardsFragment = ({ searchText, offset = 0 }) => {
  const frag = {
    '@network/boards :as boards': {
      keys: networkBoardAttrs,
      limit: 10,
      offset
    }
  }

  if (searchText) {
    frag['@network/boards :as boards'].query = searchText
  }

  return frag
}

export const networkDefaultsFragment = ({ _ }) => ({
  '@network/count-boards': {},
  '@network/default-board': {
    keys: [
      ':db/id',
      ':board/squuid',
      ':board/title',
      { ':board/network': [':db/id', ':network/title', ':network/name'] }
    ]
  },
  ':network/default-board': {
    keys: networkBoardAttrs
  }
})

export const networkSubNetworksFragment = ({ _ }) => ({
  '@network/sub-networks': {
    keys: baseNetworkAttrs.concat([
      {
        ':network/owner': baseUserAttrs,
        '@network/count-members': {},
        '@network/count-boards': {}
      }
    ])
  }
})

export const networkAdminFragment = start => ({
  ':network/lti-credentials': [
    ':db/id',
    ':lti-credential/key',
    ':lti-credential/secret'
  ],
  '@network/activity': {
    start,
    limit: 10,
    keys: activityFragment()
  }
})

export const networkStatsFragment = ({
  alias,
  networkName,
  startDate,
  endDate
}) => ({
  [`@network/statistics as ${alias || 'user'}`]: {
    networkName,
    startDate,
    endDate
  }
})

export const networkSettingsFragment = ({ _ }) => ({
  '@network/contract-start-date': {},
  '@network/contract-end-date': {},
  ':network/lti-credentials': [
    ':db/id',
    ':lti-credential/key',
    ':lti-credential/secret'
  ],
  ':network/lti-settings': []
})

export const userProfileFragment = ({ query }) => ({
  '@user/viewable-users :as profile': {
    ...queryParamsToUserId(query, true),
    keys: [
      ...baseUserAttrs,
      {
        ':user/emails': [':db/id', ':email/text', ':email/verified']
      },
      ':user/primary-email',
      ':system/created-at',
      ':user/cover-photo',
      ':user/desc',
      ':user/website',
      {
        ':user/networks': [
          ':db/id',
          { '@network/features :as :network/features': {} }
        ]
      },
      {
        ':user/location': [':db/id', ':location/city', ':location/state'],
        '@user/follows-user': {},
        '@user/follow-count': {},
        '@user/followed-by-count': {}
      }
    ]
  }
})

export const pendingNetworkBoardFragment = offset => ({
  '@network/count-pending-boards': {},
  '@network/pending-boards': {
    keys: [
      ':db/id',
      ':board/squuid',
      ':board/title',
      ':system/created-at',
      {
        ':board/created-by': baseUserAttrs,
        '@board/count-posts': {}
      }
    ],
    '@limit': 10,
    '@offset': offset
  }
})

export const loggedInUserFragment = ({ _ }, subFragment = {}) => [
  {
    '@loggedInUser :as user': {
      keys: [
        ...baseUserAttrs,
        {
          ':user/networks :as network': baseNetworkAttrs
        },
        { ...subFragment }
      ]
    }
  }
]

export const conversationsFragment = () => [
  {
    '@loggedInUser': {
      keys: [
        ':user/username',
        {
          '@user/message-threads': {
            keys: [
              ':db/id',
              ':thread/title',
              ':thread/created-by',
              {
                ':thread/network': [
                  ':db/id',
                  { '@network/features :as :network/features': {} }
                ],
                ':thread/members': baseUserAttrs
              }
            ]
          }
        }
      ]
    }
  }
]

export const sharedConversationFragment = friendIds => [
  {
    '@loggedInUser': {
      keys: [
        ':user/username',
        {
          '@user/shared-message-threads': {
            friendIds,
            keys: [
              ':db/id',
              ':thread/title',
              ':thread/created-by',
              {
                ':thread/network': [
                  ':db/id',
                  { '@network/features :as :network/features': {} }
                ],
                ':thread/members': baseUserAttrs
              }
            ]
          }
        }
      ]
    }
  }
]

export const messagesFragment = () => [
  ':db/id',
  {
    '@conversations/thread-messages': {
      keys: [
        ':db/id',
        ':system/created-at',
        ':thread-message/body',
        {
          ':thread-message/author': baseUserAttrs,
          ':thread-message/views': ['*']
        }
      ]
    }
  }
]

export const trendingUnviewedPostsFragment = ({ fromDate, networkUuid }) => ({
  '@network/trending-unviewed-posts as trendingPosts': {
    limit: 5,
    fromDate,
    networkUuid,
    keys: [
      ':db/id',
      ':event/squuid',
      ':post/title',
      { ':post/board': [':db/id', ':board/squuid'] }
    ]
  }
})

export const networkTreeFragment = ({ query, networkName }, subFragment) => ({
  '@network/tree-nodes :as networkTree': {
    networkName,
    keys: R.prop('id', query)
      ? networkTreeAttrs
      : networkTreeLtiAttrs(networkName)
  },
  ...subFragment
})

export const nudgesFragment = (ctx, subFragment) => ({
  '@board/recent-nudges': {
    keys: [
      ':db/id',
      {
        ':nudge/user': baseUserAttrs
      },
      ':nudge/type',
      ':system/created-at'
    ]
  },
  '@board/trending-post': {
    keys: [
      ':db/id',
      ':event/squuid',
      ':post/title',
      {
        '@post/author': {
          keys: baseUserAttrs
        }
      }
    ]
  },
  ...subFragment
})

export const userLatestRegisteredBoardFragment = userId => ({
  '@user/latest-registered-board :as latestBoard': {
    userId,
    keys: [':board/squuid', { ':board/network': [':network/name'] }]
  }
})
